<template>
    <feature v-slot="{ id }" feature-key="interim" message-if-no-access sub-key="detail">
        <loading v-if="fetchingInterim"/>
        <div v-else-if="!interim">
            {{ $t('interims.not_found') }}
        </div>
        <div v-else :id="id" class="w-full xl:w-5/6 max-w-1150 mx-auto">
            <div
                class="block shadow bg-white rounded-6 p-20 mb-16 border border-white">
                <div class="flex items-center">
                    <div class="border-r border-primary-20 pr-16">
                        <img :src="interim.logo" :alt="interim.name" class="w-104 object-contain">
                    </div>
                    <div class="grow pl-16 flex items-center justify-between gap-16 py-8">
                        <h1 class="text-heading-2">{{ interim.name }}</h1>
                        <div v-if="matrixValues.length > 0">
                            <simple-dropdown
                                width="auto"
                                @opened="trackMatrixView"
                            >
                                <template #trigger="{ isOpen }">
                                    <simple-button
                                        icon="keyboard-arrow-down"
                                        small
                                    >
                                        {{ $t('interims.open_matrix') }}
                                    </simple-button>
                                </template>
                                <template #content>
                                    <div class="max-w-920 overflow-x-auto bg-white rounded shadow relative flex">
                                        <div
                                            class="flex-1 text-center odd:bg-primary-5"
                                            :key="value.id"
                                            v-for="value in matrixValues"
                                        >
                                            <div class="p-8 whitespace-nowrap">
                                                {{ value.name }}
                                            </div>
                                            <div class="p-8 pt-0 font-semibold">
                                                {{ value.value }}
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </simple-dropdown>
                        </div>
                    </div>
                    <div
                        class="pl-16 flex justify-end"
                    >
                        <div v-if="interim.contact_mail">
                            <p class="font-semibold first-letter:uppercase">
                                {{ $t('interims.email') }}
                            </p>

                            <div class="flex items-center gap-12">
                                <simple-button
                                    :href="`mailto:${interim.contact_mail}`"
                                    icon="mail"
                                    icon-position="left"
                                    look="text"
                                    lowercase
                                    type="link"
                                >
                                    {{ interim.contact_mail}}
                                </simple-button>
                                <simple-button
                                    :href="`tel:${interim.contact_phone}`"
                                    icon="smartphone"
                                    icon-position="left"
                                    look="text"
                                    lowercase
                                    type="link"
                                    v-if="interim.contact_phone"
                                >
                                    {{ interim.contact_phone }}
                                </simple-button>
                            </div>
                        </div>
                        <div v-else>
                            <p class="font-semibold">{{ $t('interims.no_default_user') }}</p>
                        </div>
                    </div>
                    <div
                        class="flex items-center justify-end pl-32 pr-16"
                        v-if="canManage"
                    >
                        <simple-button
                            icon="edit"
                            look="icon"
                            @click="startEditing"
                        />
                    </div>
                </div>
            </div>
            <interim-connection
                :can-manage="canManage"
                :interim="interim"
                class="mb-16"
                @update="startEditing"
            />
            <div class="grid gap-16 grid-cols-2 mb-32">
                <interim-attachments :can-manage="canManage" :interim="interim"/>
                <interim-notes :can-manage="canManage" :interim="interim"/>
            </div>
            <div>
                <div class="mb-24">
                    <div class="flex justify-between items-center">
                        <div>
                            <h2 class="flex items-center font-semibold text-heading-2 mb-8">
                                <svg-icon class="text-secondary w-18 h-18 mr-8" type="share"/>
                                {{ $t('interims.shared_applications') }}
                            </h2>
                            <p class="text-primary-80 mb-16">
                                {{ $t('interims.shared_applications_desc', {name: interim.name}) }}
                            </p>
                        </div>
                        <simple-button
                            look="primary"
                            icon="add-circle"
                            @click="showShareJobWithInterims = true"
                        >
                            {{ $t('interims.share_jobs.share_new') }}
                        </simple-button>
                    </div>

                    <router-link
                        v-for="(job, i) in jobs"
                        :key="job.id"
                        :class="[i !== 0 ? 'border-t-0' : 'rounded-t', i === jobs.length - 1 ? 'rounded-b' : '']"
                        :to="{ name: 'job', params: { id: job.id } }"
                    >
                        <div
                            class="mb-16 rounded-6 bg-white relative border border-white trans-ease shadow px-32 pt-32 pb-24"
                            style="min-width: 1px;"
                            v-if="!jobSharingClosed(job.id)"
                        >
                            <h3 class="text-body mr-8 mb-8 hover:underline">
                                <span class="font-semibold">
                                    {{ translated(job.title, locale, true) }}
                                </span>
                            </h3>
                            <div class="text-primary-80 flex items-center mb-8">
                                <template v-if="job.active_location && job.active_location.city">
                                    <svg-icon type="location-on" class="max-w-16 max-h-16 mr-4"></svg-icon>
                                    <span class="mr-12">
                                        {{ job.active_location.city }}
                                    </span>
                                </template>
                                <svg-icon type="watch" class="max-w-16 max-h-16 mr-4"></svg-icon>
                                {{ $t('interims.shared_on', {date: getLatestMailDate(job.id)}) }}
                            </div>
                            <tag
                                :display="tagDisplay(job)"
                                no-icon
                            >
                                {{ tagDescription(job) }}
                            </tag>
                        </div>
                    </router-link>
                </div>
            </div>

            <add-interim v-if="editing" :interim="interim" @close="stopEditing"/>

            <share-job-with-interims
                v-if="showShareJobWithInterims"
                :interim="interim"
                @close="closeJobSharing"/>
        </div>
    </feature>
</template>

<script>
import SvgIcon from '../components/ui/SvgIcon';
import RoundButton from '../components/ui/RoundButton';
import * as interims from '../services/InterimService';
import {normalizeJson} from '../../shared/helpers';
import Interim from '../models/Interim';
import Loading from '../components/ui/Loading';
import AddInterim from '../components/ui/AddInterim';
import PopupModal from '../components/ui/PopupModal';
import InterimNotes from '../components/InterimNotes';
import InterimAttachments from '../components/InterimAttachments';
import {mapState} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../state/store';
import moment from 'moment';
import SimpleDropdown from '../components/ui/SimpleDropdown';
import Feature from '../components/Feature';
import InterimConnection from '../components/InterimConnection.vue';
import {trackMatrixView} from '../etc/trackers';
import SimpleButton from "../components/ui/SimpleButton.vue";
import ShareJobWithInterims from "../components/ShareJobWithInterims.vue";
import Tag from "../components/ui/Tag.vue";

export default {
    components: {
        Tag,
        ShareJobWithInterims,
        SimpleButton,
        Feature,
        SimpleDropdown, InterimAttachments, InterimNotes, PopupModal, AddInterim, Loading, RoundButton, SvgIcon,
        InterimConnection,
    },
    data() {
        return {
            fetchingInterim: false,
            editing: false,
            showShareJobWithInterims: false,
        };
    },
    computed: {
        ...mapState(useMainStore, ['locale']),
        canManage() {
            return this.$can('interims.manage');
        },
        interim() {
            return useRepo(Interim)
                .where('id', parseInt(this.$route.params.id))
                .with('matrixValues', (q) => {
                    q.with('type')
                })
                .with('internal_notes')
                .with('attachments')
                .with('interimJobs')
                .with('jobs', (q) => {
                    q.with('interimJobs')
                })
                .first();
        },
        jobs() {
            return this.interim.jobs
                .filter((age, index, arr) => arr.indexOf(age) === index)
                .sort((a, b) => {
                    const interimJobA = a.interimJobs.filter((interimJob) => interimJob.interim_id === this.interim.id)[0].created_at;
                    const interimJobB = b.interimJobs.filter((interimJob) => interimJob.interim_id === this.interim.id)[0].created_at;
                    return moment(interimJobB).unix() - moment(interimJobA).unix();
                });
        },
        matrixValues() {
            const matrixValues = this.interim.matrixValues.sort((a, b) => a.type.order - b.type.order);
            return matrixValues.map(value => ({
                id: value.id,
                name: value.type.name,
                value: value.value,
            }));
        },
    },
    methods: {
        jobSharingClosed(jobId) {
            return this.interim.interimJobs.find(interimJob => interimJob.job_id === jobId).closed_at != null;
        },
        jobIsAlwaysVisible(jobId) {
            return this.interim.interimJobs.find(interimJob => interimJob.job_id === jobId).published;
        },
        fetch(withLoading = true) {
            if (withLoading) {
                this.fetchingInterim = true;
            }

            return interims.show(parseInt(this.$route.params.id))
                .then(result => {
                    return useRepo(Interim).save(normalizeJson(result));
                })
                .finally(() => {
                    this.fetchingInterim = false;
                });
        },
        startEditing() {
            stopHsTracking();
            this.editing = true;
        },
        stopEditing() {
            startHsTracking();
            this.editing = false;
        },
        tagDisplay(job) {
            if (job.is_published) return 'success';
            if (job.archived) return 'danger';
            if (this.jobIsAlwaysVisible(job.id)) return 'success';
            if (!job.is_published) return 'danger';
            if (this.jobSharingClosed(job.id)) return 'danger';
        },
        tagDescription(job) {
            if (job.is_published) return this.$t('interims.job_status_published');
            if (job.archived) return this.$t('interims.job_status_archived');
            if (this.jobIsAlwaysVisible(job.id)) return this.$t('interims.job_status_un_published_available');
            if (!job.is_published) return this.$t('interims.job_status_un_published');
            if (this.jobSharingClosed(job.id)) return this.$t('interims.job_status_closed');
        },
        getLatestMailDate(jobId) {
            const latest = this.interim.interimJobs.filter(i => i.job_id === jobId).sort((a, b) => moment(b.created_at).unix() - moment(a.created_at).unix())[0];

            if (!latest) {
                return null;
            }

            return moment(latest.created_at).locale(this.locale).format('DD MMMM YYYY HH:SS');
        },
        closeJobSharing(updated) {
            this.showShareJobWithInterims = false;

            if (updated) {
                this.fetch(false);
            }
        },
        trackMatrixView,
    },
    mounted() {
        this.fetch();
    },
};
</script>
